// library imports
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Tooltip,
  Modal,
  Divider,
  Flex,
  Spin,
  Select,
  Popconfirm,
} from "antd";
import { useEffect, useState, useContext } from "react";
// component imports
import PieceProductForm from "./PieceProductForm";
import DataTable from "../../../components/common/DataTable";
// context imports
import { IndexContext } from "../../../context/IndexContext";
// hook imports
import useFetch from "../../../hooks/useFetch";

export default function PieceProductView() {
  // index context
  const {
    siteSelected,
    setSiteSelected,
    siteList,
    getSiteSelectionLoading,
    messageApi,
  } = useContext(IndexContext);

  // fetch wrapper
  const [fetchWrapper] = useFetch();

  // piece product data to be displayed in table
  const [pieceProductDetails, setPieceProductDetails] = useState([]);

  // piece product table loading state
  const [pieceProductTableLoading, setPieceProductTableLoading] =
    useState(false);

  // add modal opening state
  const [isAddPieceProductModalOpen, setIsAddPieceProductModalOpen] =
    useState(false);

  // edit modal opening state
  const [isEditPieceProductModalOpen, setIsEditPieceProductModalOpen] =
    useState(false);

  // edit form initial values
  const [formInitialValues, setFormInitialValues] = useState({});

  // year list to be disabled for date picker in piece product form
  const [disabledYears, setDisabledYears] = useState([]);

  const pieceProductTableColumns = [
    {
      title: "Year",
      dataIndex: "Year",
      key: "Year",
    },
    {
      title: "Products Produced (Pieces)",
      dataIndex: "PieceProduct",
      key: "PieceProduct",
    },
    {
      title: "",
      dataIndex: "Action",
      key: "Action",
      width: "11%",
      render: (_, record) => (
        <Flex justify="flex-start" align="center" gap={"5px"} wrap={true}>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "blue" }} />}
              onClick={() => {
                getPieceProductFormData(record);
                setIsEditPieceProductModalOpen(true);
              }}
            />
          </Tooltip>

          <Tooltip title="Delete">
            <Popconfirm
              title="Delete Products Produced (Pieces)"
              description={
                "Are you sure to delete this products produced (pieces)?"
              }
              onConfirm={() => {
                deletePieceProduct(record);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<DeleteOutlined style={{ color: "red" }} />}
              />
            </Popconfirm>
          </Tooltip>
        </Flex>
      ),
    },
  ];

  /* API to get piece product form data to initialise form when form is in 'edit' mode */
  function getPieceProductFormData(row_record) {
    fetchWrapper({
      method: "GET",
      endpoint_url: "piece_product_management/getPieceProductFormData",
      set_content_type_header: false,
      params: {
        piece_product_id: row_record.ID,
      },
      onSuccess: (fetch_output) => {
        setFormInitialValues(fetch_output.data);
      },
    });
  }

  /* API to get piece product table data */
  function getPieceProductDetails() {
    setPieceProductTableLoading(true);
    fetchWrapper({
      method: "GET",
      endpoint_url: "piece_product_management/getPieceProductDetails",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        setPieceProductTableLoading(false);
        setPieceProductDetails(fetch_output.data);
      },
    });
  }

  /** API to delete piece product */
  function deletePieceProduct(row_record) {
    setPieceProductTableLoading(true);
    fetchWrapper({
      endpoint_url: "piece_product_management/deletePieceProduct",
      body: {
        piece_product_id: row_record.ID,
      },
      onSuccess: (fetch_output) => {
        setPieceProductTableLoading(false);
        if (fetch_output.data === true) {
          messageApi.open({
            type: "success",
            content: fetch_output.message,
          });
          getPieceProductDetails();
          getYearList();
        } else {
          messageApi.open({
            type: "error",
            content: fetch_output.message,
          });
        }
      },
    });
  }

  /* API to get year list of the selected site which the piece product has been input */
  function getYearList() {
    fetchWrapper({
      method: "GET",
      endpoint_url: "piece_product_management/getYearList",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        setDisabledYears(fetch_output.data);
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      getPieceProductDetails();
      getYearList();
    }
  }, [siteSelected]);

  return (
    <>
      <Flex align="center">
        <p className="mr-2">Site:</p>
        <Select
          style={{ width: "100%", textAlign: "left" }}
          loading={getSiteSelectionLoading}
          showSearch
          value={siteSelected}
          options={siteList}
          optionFilterProp="label"
          labelRender={(value) => <>{value.label}</>}
          onChange={(value) => {
            setSiteSelected(value);
          }}
        />
      </Flex>

      <Flex>
        <Button
          icon={<PlusOutlined />}
          className="mt-3 mb-5"
          onClick={() => setIsAddPieceProductModalOpen(true)}
          disabled={pieceProductTableLoading}
        >
          Product Produced (Piece)
        </Button>
      </Flex>

      {pieceProductTableLoading === true ? (
        <Flex justify="center" align="center" style={{ height: "50vh" }}>
          <Spin />
        </Flex>
      ) : (
        <DataTable
          table_column={pieceProductTableColumns}
          table_data={pieceProductDetails}
          scroll_width={1500}
        />
      )}

      <Modal
        title="Add Product Produced (Piece)"
        open={isAddPieceProductModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <PieceProductForm
          mode="add"
          setIsModalOpen={setIsAddPieceProductModalOpen}
          refreshTable={getPieceProductDetails}
          refreshYearList={getYearList}
          siteSelected={siteSelected}
          disabledYears={disabledYears}
        />
      </Modal>

      <Modal
        title="Edit Product Produced (Piece)"
        open={isEditPieceProductModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <PieceProductForm
          mode="edit"
          setIsModalOpen={setIsEditPieceProductModalOpen}
          refreshTable={getPieceProductDetails}
          initialValues={formInitialValues}
          disabledYears={disabledYears}
        />
      </Modal>
    </>
  );
}
